import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';
import { DateTime } from 'luxon';

import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { TableState } from 'dashboard/lib/interfaces/TableState';
import { Case } from 'dashboard/models/case';
import { CaseQueue } from 'dashboard/models/case-queue';
import { PaginationParams } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

export type FilterValues = string[] | 'all';

export interface CaseQueueCasesFilterParams {
  startDate: Iso8601DateString | undefined;
  endDate: Iso8601DateString | undefined;
  dateType: 'created_at';
  assigneeIds: FilterValues;
  teamAssigneeIds: FilterValues;
  omni: string | undefined;
  isInitial: boolean;
}

interface CaseQueueCasesTableState extends TableState<CaseQueueCasesFilterParams> {
  pageSize: number;
  currentPage: number;
  filters: CaseQueueCasesFilterParams;
}

export const getInitialQueueCasesFilterState = (): CaseQueueCasesFilterParams => {
  const currentTime = DateTime.local();
  return {
    startDate: currentTime.minus({ weeks: 2 }).startOf('day').toISO(),
    endDate: currentTime.endOf('day').toISO(),
    dateType: 'created_at',
    assigneeIds: [],
    teamAssigneeIds: [],
    omni: undefined,
    isInitial: true,
  };
};

const getInitialQueueCasesTableState = (): CaseQueueCasesTableState => ({
  paginationParams: undefined,
  pageSize: DEFAULT_PAGE_SIZE,
  currentPage: 1,
  filters: getInitialQueueCasesFilterState(),
});

export interface CaseQueueState {
  caseQueue: CaseQueue | undefined;
  cases: {
    cases: Case[];
    tableState: CaseQueueCasesTableState;
  };
}

// Load without start and end date because we haven't loaded user's timezone yet
export const initialState: CaseQueueState = {
  caseQueue: undefined,
  cases: {
    cases: [],
    tableState: {
      paginationParams: undefined,
      pageSize: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      filters: {
        startDate: undefined,
        endDate: undefined,
        dateType: 'created_at',
        assigneeIds: [],
        teamAssigneeIds: [],
        omni: undefined,
        isInitial: true,
      },
    },
  },
};

interface SetCaseQueuePayload {
  caseQueue: CaseQueue;
}

interface SetCasesPayload {
  cases: Case[];
}

interface SetCasesCurrentPagePayload {
  currentPage: number;
}

interface SetCasesPageSizePayload {
  pageSize: number;
}

interface SetCasesPaginationParamsPayload {
  paginationParams: PaginationParams;
}

interface SetCasesFiltersPayload {
  filters: Partial<CaseQueueCasesFilterParams>;
}

const { reducer, actions } = createSlice({
  name: 'case-queue',
  initialState,
  reducers: {
    clearCaseQueue: () => {
      return initialState;
    },
    setCaseQueue: (state, action: PayloadAction<SetCaseQueuePayload>) => {
      state.caseQueue = action.payload.caseQueue;
    },
    setCases: (state, action: PayloadAction<SetCasesPayload>) => {
      state.cases.cases = castDraft(action.payload.cases);
    },
    setCasesCurrentPage: (state, action: PayloadAction<SetCasesCurrentPagePayload>) => {
      state.cases.tableState.currentPage = action.payload.currentPage;
    },
    setCasesPageSize: (state, action: PayloadAction<SetCasesPageSizePayload>) => {
      state.cases.tableState.pageSize = action.payload.pageSize;
    },
    setCasesPaginationParams: (state, action: PayloadAction<SetCasesPaginationParamsPayload>) => {
      state.cases.tableState.paginationParams = action.payload.paginationParams;
    },
    setCasesFilters: (state, action: PayloadAction<SetCasesFiltersPayload>) => {
      state.cases.tableState.filters = {
        ...state.cases.tableState.filters,
        ...action.payload.filters,
      };
    },
    clearCasesFilters: (state) => {
      state.cases.tableState = getInitialQueueCasesTableState();
    },
  },
});

export const {
  clearCaseQueue,
  setCaseQueue,
  setCases,
  setCasesCurrentPage,
  setCasesPageSize,
  setCasesPaginationParams,
  setCasesFilters,
  clearCasesFilters,
} = actions;

export const caseQueueReducer = reducer;
